import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FolderKanban, Calendar, AlignLeft, Tag, LayoutGrid, X, User } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: 'backlog', label: 'Backlog', color: 'bg-slate-400' },
  { value: 'em_andamento', label: 'Em Andamento', color: 'bg-blue-500' },
  { value: 'revisao', label: 'Revisão', color: 'bg-yellow-500' },
  { value: 'entregue', label: 'Entregue', color: 'bg-green-500' },
];

const PRIORITY_OPTIONS = [
  { value: 'baixa', label: 'Baixa', color: 'text-slate-600', bg: 'bg-slate-100' },
  { value: 'media', label: 'Média', color: 'text-blue-700', bg: 'bg-blue-100' },
  { value: 'alta', label: 'Alta', color: 'text-orange-700', bg: 'bg-orange-100' },
  { value: 'urgente', label: 'Urgente', color: 'text-red-700', bg: 'bg-red-100' },
];

const TYPE_OPTIONS = [
  { value: 'website', label: 'Website' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'branding', label: 'Branding' },
  { value: 'marketing_digital', label: 'Marketing Digital' },
  { value: 'consultoria', label: 'Consultoria' },
  { value: 'outro', label: 'Outro' },
];

export default function ProjetoCreateModal({ clients, isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    client_id: '',
    name: '',
    description: '',
    project_type: 'outro',
    priority: 'media',
    status: 'backlog',
    start_date: '',
    deadline: ''
  });
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setFormData({
      client_id: '',
      name: '',
      description: '',
      project_type: 'outro',
      priority: 'media',
      status: 'backlog',
      start_date: '',
      deadline: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        client_id: formData.client_id || null,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null
      };
      await api.post('/projects', payload);
      toast.success('Projeto criado com sucesso!');
      resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Erro ao criar projeto');
    } finally {
      setLoading(false);
    }
  };

  const currentStatus = STATUS_OPTIONS.find(s => s.value === formData.status);
  const currentPriority = PRIORITY_OPTIONS.find(p => p.value === formData.priority);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl p-0 overflow-hidden border-0 shadow-2xl rounded-2xl gap-0 [&>button:last-child]:hidden">
        {/* Header gradient */}
        <div className="bg-gradient-to-br from-violet-600 to-indigo-700 px-6 pt-6 pb-8 text-white relative">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <X className="h-4 w-4 text-white" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              <FolderKanban className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-medium text-white/70">Novo Projeto</span>
          </div>
          <h2 className="text-xl font-bold text-white leading-tight pr-8">
            {formData.name || 'Sem nome ainda'}
          </h2>
          <div className="flex items-center gap-2 mt-3">
            {currentStatus && (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/20 text-white px-2.5 py-1 rounded-full">
                <span className={`w-2 h-2 rounded-full ${currentStatus.color}`}></span>
                {currentStatus.label}
              </span>
            )}
            {currentPriority && (
              <span className={`inline-flex items-center text-xs font-bold px-2.5 py-1 rounded-full ${currentPriority.bg} ${currentPriority.color}`}>
                {currentPriority.label}
              </span>
            )}
          </div>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="bg-white px-6 py-5 flex flex-col gap-4">

          {/* Cliente + Nome */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" /> Cliente
              </Label>
              <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                <SelectTrigger className="h-10 border-slate-200">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <FolderKanban className="h-3.5 w-3.5" /> Nome do Projeto *
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Ex: Website institucional..."
                className="border-slate-200 h-10"
                data-testid="new-project-name"
              />
            </div>
          </div>

          {/* Status + Prioridade */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <LayoutGrid className="h-3.5 w-3.5" /> Status
              </Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="h-10 border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${s.color}`}></span>
                        {s.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <Tag className="h-3.5 w-3.5" /> Prioridade
              </Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                <SelectTrigger className="h-10 border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map(p => (
                    <SelectItem key={p.value} value={p.value}>
                      <span className={`font-semibold ${p.color}`}>{p.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tipo */}
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Tipo de Projeto</Label>
            <Select value={formData.project_type} onValueChange={(v) => setFormData({ ...formData, project_type: v })}>
              <SelectTrigger className="h-10 border-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Datas */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> Data Início
              </Label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="h-10 border-slate-200"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> Prazo Final
              </Label>
              <Input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                className="h-10 border-slate-200"
              />
            </div>
          </div>

          {/* Descrição */}
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
              <AlignLeft className="h-3.5 w-3.5" /> Descrição
            </Label>
            <textarea
              className="w-full min-h-[80px] px-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent resize-y transition-colors"
              placeholder="Descrição do projeto..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-200 text-slate-600">
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              data-testid="submit-new-project-btn"
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-md"
            >
              {loading ? 'Criando...' : 'Criar Projeto'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
